<script setup lang="ts">
import { ref, watch, computed } from 'vue'
import { router } from '@inertiajs/vue3'
import Dialog from 'primevue/dialog'
import AutoComplete from 'primevue/autocomplete'
import InputNumber from 'primevue/inputnumber'
import Textarea from 'primevue/textarea'
import { notify } from '@kyvg/vue3-notification'

import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faCommentAltExclamation, faInfoCircle, faSpinner, faExclamationTriangle } from '@fal'
import { library } from '@fortawesome/fontawesome-svg-core'
import axios from 'axios'
library.add(faCommentAltExclamation, faInfoCircle, faSpinner, faExclamationTriangle)

import { useFormatTime } from '@/Composables/useFormatTime'
import Icon from '@/Components/Utils/Icon.vue'
import Button from '@/Components/Utils/Button.vue'
import { Icon as IconTS } from '@/types/Icon'

type OrderOption = {
    id: number
    reference: string
    customer?: { name: string }
    billing_status: string
    billing_status_label: string
    type?: string
    type_icon?: IconTS
    product_name?: string
    quantity?: number
    dimension_width?: number
    dimension_height?: number
    created_at?: string
    manufacture_finished_by?: string
    has_complaint?: boolean
    complaints_count?: number
    file_merek?: string
}

const props = defineProps<{
    visible: boolean
    prefillOrder?: OrderOption
}>()

const emit = defineEmits<{
    'update:visible': [value: boolean]
}>()

const selectedOrder = ref<OrderOption | null>(null)
const queryOrder    = ref('')
const quantity      = ref<number>(1)
const note          = ref('')
const isSubmitting  = ref(false)
const suggestions   = ref<OrderOption[]>([])
const isFetching    = ref(false)

const searchOrders = async (query: string) => {
    isFetching.value = true
    try {
        const res = await axios.get(route('json.orders.manufacture_finished'), { params: { q: query } })
        const json = res.data
        suggestions.value = json.data ?? json
    } catch {
        notify({ title: 'Error', text: 'Gagal memuat daftar order', type: 'error' })
    } finally {
        isFetching.value = false
    }
}

watch(() => props.visible, (val) => {
    if (val) {
        selectedOrder.value = props.prefillOrder ?? null
        queryOrder.value    = props.prefillOrder?.reference ?? ''
        quantity.value      = 1
        note.value          = ''
        suggestions.value   = []
    } else {
        selectedOrder.value = null
        queryOrder.value    = ''
        quantity.value      = 1
        note.value          = ''
        suggestions.value   = []
    }
})

const close = () => emit('update:visible', false)

const submit = () => {
    if (!selectedOrder.value?.id) {
        notify({ title: 'Error', text: 'Pilih order terlebih dahulu', type: 'error' })
        return
    }

    isSubmitting.value = true

    router.post(route('layout.complaints.store'), {
        order_id: selectedOrder.value?.id,
        quantity: quantity.value,
        note: note.value || undefined,
    }, {
        onSuccess: () => {
            close()
            notify({ title: 'Berhasil', text: 'Komplain berhasil dibuat', type: 'success' })
        },
        onError: (errors) => {
            const firstError = Object.values(errors)[0]
            notify({ title: 'Gagal', text: String(firstError), type: 'error' })
        },
        onFinish: () => { isSubmitting.value = false },
    })
}
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="emit('update:visible', $event)"
        header="Buat Komplain"
        modal
        :style="{ width: '480px' }"
        :draggable="false"
    >
        <div class="flex flex-col gap-4 pt-2">

            <!-- Info banner (only shown when searching, not when order is pre-filled) -->
            <div v-if="!prefillOrder" class="flex items-start gap-2 rounded-lg bg-sky-50 border border-sky-200 px-3 py-2.5 text-sm text-sky-700">
                <FontAwesomeIcon icon="fal fa-info-circle" class="mt-0.5 shrink-0" fixed-width aria-hidden="true" />
                <span>Hanya menampilkan order yang <strong>sudah selesai produksi</strong> dalam 1 bulan terakhir.</span>
            </div>

            <!-- Order -->
            <div>
                <label class="text-sm font-medium mb-1 block">
                    Order <span class="text-red-500">*</span>
                </label>

                <AutoComplete
                    v-if="!prefillOrder"
                    v-model="queryOrder"
                    :suggestions="suggestions"
                    optionLabel="reference"
                    placeholder="Cari referensi order..."
                    class="w-full"
                    inputClass="w-full"
                    :loading="isFetching"
                    :minLength="0"
                    forceSelection
                    @complete="searchOrders($event.query)"
                    completeOnFocus
                    @option-select="(e) => { selectedOrder = e.value }"
                >
                    <template #option="{ option }">
                        <div class="flex items-center justify-between w-full gap-3">
                            <div class="flex flex-col">
                                <span class="font-medium flex items-center gap-1.5">
                                    <Icon v-if="option.type_icon" :data="option.type_icon" />
                                    {{ option.reference }}
                                    <span
                                        v-if="option.has_complaint"
                                        class="text-xs font-semibold px-1.5 py-0.5 rounded bg-orange-100 text-orange-700"
                                        title="Sudah pernah dikomplain"
                                    >{{ option.complaints_count }}x komplain</span>
                                </span>
                                <span class="text-xs text-gray-400">{{ option.customer?.name ?? '-' }}</span>
                            </div>
                            <span
                                class="text-xs font-semibold px-2 py-0.5 rounded-full shrink-0"
                                :class="option.billing_status === 'paid'
                                    ? 'bg-green-100 text-green-700'
                                    : 'bg-red-100 text-red-700'"
                            >
                                {{ option.billing_status_label }}
                            </span>
                        </div>
                    </template>
                    <template #empty>
                        <span class="text-sm text-gray-400 px-2">
                            {{ isFetching ? 'Memuat...' : 'Tidak ada order ditemukan' }}
                        </span>
                    </template>
                </AutoComplete>


                <!-- Detail singkat order yang dipilih -->
                <div
                    vxif="selectedOrder"
                    class="mt-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 grid grid-cols-2 gap-x-4 gap-y-4 text-sm"
                >
                    <div>
                        <span class="text-gray-400 text-xs block">Order reference</span>
                        <span class="font-medium">{{ selectedOrder?.reference ?? '-' }}</span>
                    </div>
                    <div class="xcol-span-2">
                        <span class="text-gray-400 text-xs block">Pelanggan</span>
                        <span class="font-medium">{{ selectedOrder?.customer?.name ?? '-' }}</span>
                    </div>

                    <div class="col-span-2 grid grid-cols-2 gap-x-4">
                        <div>
                            <span class="text-gray-400 text-xs block">Produk</span>
                            <span class="font-medium flex items-center gap-1.5">
                                <Icon v-if="selectedOrder?.type_icon" :data="selectedOrder?.type_icon" />
                                {{ selectedOrder?.product_name ?? '-' }}
                            </span>
                        </div>

                        <div v-if="selectedOrder?.type === 'flexo'" class="">
                            <span class="text-gray-400 text-xs block">Dimensi (cm)</span>
                            <span class="font-medium">
                                {{ selectedOrder?.dimension_width ?? '-' }} × {{ selectedOrder?.dimension_height ?? '-' }} cm
                            </span>
                        </div>
                    </div>
                    
                    <div class="col-span-2 grid grid-cols-2 gap-x-4">
                        <div class="">
                            <span class="text-gray-400 text-xs block">Kuantitas</span>
                            <span class="font-medium">{{ selectedOrder?.quantity ?? '-' }}</span>
                        </div>
                        
                        <div v-if="selectedOrder?.file_merek" class="">
                            <span class="text-gray-400 text-xs block">File Merek</span>
                            <span class="font-medium">{{ selectedOrder?.file_merek }}</span>
                        </div>
                    </div>

                    <div>
                        <span class="text-gray-400 text-xs block">Tgl. Dibuat</span>
                        <span class="font-medium">{{ selectedOrder?.created_at ? useFormatTime(selectedOrder?.created_at) : '-' }}</span>
                    </div>

                    <div>
                        <span class="text-gray-400 text-xs block">Diselesaikan oleh</span>
                        <span class="font-medium">{{ selectedOrder?.manufacture_finished_by ?? '-' }}</span>
                    </div>

                    <div v-if="selectedOrder?.has_complaint" class="col-span-2">
                        <div class="flex items-center gap-1.5 rounded-md bg-orange-50 border border-orange-200 px-2.5 py-1.5 text-sm text-orange-700">
                            <FontAwesomeIcon icon="fal fa-exclamation-triangle" fixed-width aria-hidden="true" />
                            <span>Order ini sudah pernah dikomplain sebanyak <strong>{{ selectedOrder.complaints_count }}x</strong>.</span>
                        </div>
                    </div>
                </div>
            </div>

            <!-- Jumlah Komplain -->
            <div>
                <label class="text-sm font-medium mb-1 block">
                    Jumlah Komplain <span class="text-red-500">*</span>
                </label>
                <InputNumber
                    v-model="quantity"
                    :min="1"
                    showButtons
                    fluid
                    placeholder="Jumlah item yang dikomplain"
                    :max="selectedOrder?.quantity ?? 1"
                />
            </div>

            <!-- Keterangan -->
            <div>
                <label class="text-sm font-medium mb-1 block">Keterangan</label>
                <Textarea
                    v-model="note"
                    rows="3"
                    class="w-full"
                    placeholder="Deskripsi komplain (opsional)..."
                    autoResize
                />
            </div>

        </div>

        <template #footer>
            <div class="flex justify-end gap-2 w-full">
                <Button
                    type="negative"
                    label="Batal"
                    @click="close"
                />
                <Button
                    type="primary"
                    label="Buat Komplain"
                    @click="submit"
                    :disabled="!selectedOrder?.id"
                    :loading="isSubmitting"
                    full
                />
            </div>
        </template>
    </Dialog>
</template>
